import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null, ep: null, eps: [], type: "single" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let cancel = false;
    const MAX_ATTEMPTS = 20; // ~60s total window — Stripe can be slow on test mode
    const POLL_INTERVAL_MS = 3000;

    const poll = async () => {
      if (cancel || attemptsRef.current >= MAX_ATTEMPTS) {
        if (!cancel && attemptsRef.current >= MAX_ATTEMPTS) setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancel) return;
        if (data.payment_status === "paid") {
          setState({
            status: "paid",
            payment_status: "paid",
            ep: data.episode_number,
            eps: data.episode_numbers || [],
            type: data.type || "single",
          });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        // 404 = transaction doesn't exist for this user — bail immediately, no point retrying
        if (err?.response?.status === 404) {
          if (!cancel) setState({ status: "error" });
          return;
        }
        if (!cancel) setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancel = true; };
  }, [sessionId]);

  const isBundle = state.type === "bundle" && state.eps.length > 0;
  const paidHeadline = isBundle
    ? `${state.eps.length} EPISODES `
    : (state.ep ? `EPISODE ${state.ep} ` : "PAYMENT ");

  return (
    <div className="bg-ink min-h-screen flex items-center justify-center px-5" data-testid="success-page">
      <div className="max-w-lg w-full bg-coal border border-white/10 p-8 md:p-12 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 className="mx-auto text-hotpink animate-spin mb-4" size={48} />
            <h1 className="brush-title text-4xl text-white mb-2">CONFIRMING<span className="text-hotpink">.</span></h1>
            <p className="scribble text-white/70 text-xl">hang on, checking the receipts...</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-green-400 mb-4" size={48} />
            <p className="scribble text-hotpink text-2xl rotate-[-2deg] inline-block mb-2">unlocked. iconic.</p>
            <h1 className="brush-title text-5xl text-white mb-4" data-testid="success-headline">{paidHeadline}<span className="text-hotpink">YOURS.</span></h1>
            <p className="font-outfit text-white/70 mb-6">
              {isBundle
                ? `The bundle landed — episodes ${state.eps.join(", ")} are unlocked forever. Even at 2am with red wine.`
                : "Forever. Even when you re-listen at 2am with red wine."}
            </p>
            <Link to="/episodes" data-testid="success-back-to-episodes" className="inline-block bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-sm px-7 py-4 shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_#FFFFFF] transition-all">
              go listen →
            </Link>
          </>
        )}
        {(state.status === "expired" || state.status === "error") && (
          <>
            <XCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h1 className="brush-title text-4xl text-white mb-3">PAYMENT <span className="text-hotpink">DIDN'T LAND.</span></h1>
            <p className="font-outfit text-white/70 mb-6">No worries — try again or come back later.</p>
            <Link to="/episodes" className="inline-block bg-hotpink text-white font-outfit font-extrabold uppercase tracking-widest text-sm px-7 py-4 shadow-[4px_4px_0px_#FFFFFF]">
              back to episodes
            </Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Loader2 className="mx-auto text-yellow-400 mb-4" size={48} />
            <h1 className="brush-title text-4xl text-white mb-3">STILL <span className="text-hotpink">PROCESSING.</span></h1>
            <p className="font-outfit text-white/70 mb-6">Refresh the episodes page in a minute — your unlock should appear.</p>
            <Link to="/episodes" className="inline-block bg-hotpink text-white font-outfit font-extrabold uppercase tracking-widest text-sm px-7 py-4 shadow-[4px_4px_0px_#FFFFFF]">
              go to episodes
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
